import { Button } from '@contember/bindx-ui'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { type ReactNode, useEffect, useSyncExternalStore } from 'react'

/**
 * Replacement for `window.confirm`. The native one is a browser chrome box —
 * it says "localhost:1681 says", takes no formatting and looks nothing like
 * the admin. This is the same overlay + card as the "New page" dialog, on the
 * Radix dialog the edit sheets already use, so it traps focus, closes on Esc
 * and returns focus to the button that opened it.
 *
 * Usage mirrors the old call so handlers keep their shape:
 *
 *   if (!await confirmDialog({ title: 'Delete this page?' })) return
 */
export interface ConfirmRequest {
	title: string
	/** Consequences worth spelling out. Skip it when the title already says everything. */
	description?: ReactNode
	confirmLabel?: string
	cancelLabel?: string
	/** `danger` paints the confirm button red — for anything that destroys data. */
	tone?: 'default' | 'danger'
}

type PendingConfirm = ConfirmRequest & { resolve: (confirmed: boolean) => void }

let pending: PendingConfirm | null = null
const listeners = new Set<() => void>()

const emit = () => {
	for (const listener of listeners) listener()
}

const subscribe = (listener: () => void) => {
	listeners.add(listener)
	return () => {
		listeners.delete(listener)
	}
}

const getSnapshot = () => pending

export function confirmDialog(request: ConfirmRequest): Promise<boolean> {
	// A second question while one is open would leave the first caller awaiting
	// a promise nobody will ever settle, so the one being replaced answers "no".
	pending?.resolve(false)
	return new Promise<boolean>(resolve => {
		pending = { ...request, resolve }
		emit()
	})
}

/** Mounted once in the app shell, next to the toast container. */
export function ConfirmDialogHost() {
	const request = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

	const answer = (confirmed: boolean) => {
		const answered = pending
		pending = null
		emit()
		answered?.resolve(confirmed)
	}

	// If the shell goes away mid-question — a session that drops to the login
	// screen — the caller must not be left awaiting a promise forever. The
	// unsaved-changes guard holds a blocked navigation open on this answer.
	useEffect(() => () => {
		const abandoned = pending
		pending = null
		abandoned?.resolve(false)
	}, [])

	return (
		<DialogPrimitive.Root
			onOpenChange={open => {
				if (!open) answer(false)
			}}
			open={request !== null}
		>
			{request
				? (
					<DialogPrimitive.Portal>
						<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-blue-primary/45" />
						<DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-light-gray bg-white p-6 shadow-xl outline-none">
							<DialogPrimitive.Title className="font-vag-normal text-base font-semibold text-blue-primary leading-snug">
								{request.title}
							</DialogPrimitive.Title>
							{request.description
								? <DialogPrimitive.Description className="mt-2 text-sm text-gray leading-relaxed">{request.description}</DialogPrimitive.Description>
								: null}
							{
								/* Cancel first, so Radix puts the initial focus on it rather than on
							    the button that goes through with a destructive action. */
							}
							<div className="mt-6 flex justify-end gap-2">
								<Button onClick={() => answer(false)} type="button" variant="ghost">
									{request.cancelLabel ?? 'Cancel'}
								</Button>
								<Button
									className={request.tone === 'danger' ? undefined : 'btn-orange'}
									onClick={() => answer(true)}
									type="button"
									variant={request.tone === 'danger' ? 'destructive' : 'default'}
								>
									{request.confirmLabel ?? 'Confirm'}
								</Button>
							</div>
						</DialogPrimitive.Content>
					</DialogPrimitive.Portal>
				)
				: null}
		</DialogPrimitive.Root>
	)
}
